/**
 * Shared onboarding primitives for the ISATS portal.
 *
 * Centralizes the OTP-free onboarding helpers that were previously duplicated
 * across AuthPage.tsx, ReferrerOnboarding.tsx, EmployerOnboarding.tsx and
 * SponsorOnboarding.tsx: phone/URL/email validation, the canonical fallback
 * program list, the step model for the progress indicator, and user-safe
 * API-error mapping (including structured 409 conflict signals for
 * "continue" recovery flows).
 */

/* ------------------------------------------------------------------ */
/* Phone                                                               */
/* ------------------------------------------------------------------ */

/** E.164-ish: optional leading +, 7–15 digits, first digit non-zero. */
export const PHONE_REGEX = /^\+?[1-9]\d{6,14}$/;

export const phoneErrorMessage =
  'Enter a valid phone number, e.g. +1-876-555-0199.';

/**
 * Validates a phone number after stripping common formatting characters
 * (spaces, dashes, dots, parentheses).
 */
export function isValidPhone(phone: string): boolean {
  if (!phone) return false;
  const compact = phone.replace(/[\s\-().]/g, '');
  return PHONE_REGEX.test(compact);
}

/* ------------------------------------------------------------------ */
/* Email / URL                                                         */
/* ------------------------------------------------------------------ */

/** Lightweight inline email check (not a substitute for server validation). */
export function isValidEmail(email: string): boolean {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

/**
 * Validates an optional website field. Empty string is valid (optional);
 * otherwise the value must parse as an http(s) URL. A missing scheme is
 * assumed to be https.
 */
export function isValidUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return true;
  try {
    const parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Fallback programs                                                   */
/* ------------------------------------------------------------------ */

/**
 * Canonical fallback program list, deduped from the identical lists
 * previously triplicated in AuthPage.tsx, ReferrerOnboarding.tsx and
 * EmployerOnboarding.tsx. Used when the programs endpoint fails.
 */
export interface FallbackProgram {
  id: string;
  name: string;
}

export const FALLBACK_PROGRAMS: FallbackProgram[] = [
  { id: 'j1-internship', name: 'Intern' },
  { id: 'j1-trainee', name: 'Trainee' },
  { id: 'j1-swt', name: 'Summer Work Travel' },
  { id: 'j1-counselor', name: 'Camp Counselor' },
  { id: 'j1-teacher', name: 'Teacher' },
  { id: 'au-pair', name: 'Au Pair' },
  { id: 'research-scholar', name: 'Research Scholar' },
];

/* ------------------------------------------------------------------ */
/* Step model                                                          */
/* ------------------------------------------------------------------ */

export type OnboardingStepId =
  | 'verify-email'
  | 'your-details'
  | 'organization'
  | 'review'
  | 'complete';

export type OnboardingRole = 'employer' | 'referrer' | 'sponsor' | 'applicant';

export interface OnboardingStep {
  id: OnboardingStepId;
  label: string;
  /** Roles that see this step. Omitted = visible to every role. */
  roles?: OnboardingRole[];
}

/** Canonical ordered step list for the onboarding progress indicator. */
export const ONBOARDING_STEPS: OnboardingStep[] = [
  { id: 'verify-email', label: 'Verify Email' },
  { id: 'your-details', label: 'Your Details' },
  {
    id: 'organization',
    label: 'Organization',
    roles: ['employer', 'referrer', 'sponsor'],
  },
  { id: 'review', label: 'Review' },
  { id: 'complete', label: 'Complete' },
];

/** Returns the steps visible for a role (role-scoped steps are filtered out). */
export function visibleStepsForRole(role: OnboardingRole): OnboardingStep[] {
  return ONBOARDING_STEPS.filter((step) => !step.roles || step.roles.includes(role));
}

/**
 * Whether the onboarding step indicator may render.
 *
 * The indicator is part of the onboarding journey and must ONLY appear for
 * new users actively being onboarded. It must never render on sign-in
 * screens (returning users), password-reset screens, or before the auth
 * flow has established that the user is new.
 *
 * @param flow the AuthPage auth flow ('signup' | 'login' | 'password_reset')
 * @param isNewUser whether the email was established as a new signup
 */
export function isOnboardingProgressVisible(flow: string, isNewUser: boolean): boolean {
  return flow === 'signup' && isNewUser;
}

/* ------------------------------------------------------------------ */
/* Signup role resolution                                              */
/* ------------------------------------------------------------------ */

/**
 * The four public self-registration roles, in canonical tile order.
 *
 * Which of them a visitor may actually pick is a WORKSPACE decision: the
 * admin "Signup Roles" toggles in Workspace Settings write the workspace
 * doc's `signupRoles` array, and the server enforces exactly that set at
 * account creation (validateWorkspaceForSignup → 403 SIGNUP_ROLE_DISABLED;
 * it never downgrades a submitted role). The helpers below are the single
 * client/server-shared implementation of that contract:
 *
 * - A missing/empty `signupRoles` array means NO restriction (all roles) —
 *   the same reading the settings UI shows ("All roles allowed").
 * - The static brand flags (brandConfig.roleSetup, compiled from VITE_ROLE_*
 *   env vars at build time) are NOT the gate: one build serves many
 *   workspaces, so a build-time flag cannot express per-workspace policy.
 *   They survive only as the offline fallback used until the workspace's
 *   effective set has been fetched (see resolveSignupTileRoles).
 */
export const SIGNUP_ROLE_VALUES: readonly OnboardingRole[] = [
  'applicant',
  'employer',
  'referrer',
  'sponsor',
];

export function isSignupRole(value: unknown): value is OnboardingRole {
  return (
    typeof value === 'string' &&
    (SIGNUP_ROLE_VALUES as readonly string[]).includes(value)
  );
}

/**
 * Extracts a pre-set signup role from the page URL: `?role=`, `?type=` or
 * `?signupRole=` query params (the shape of the "Direct Role Signup Links"
 * in Workspace Settings), or a trailing `/signup/<role>` path segment.
 * Returns null when no valid role is present.
 */
export function parseSignupRoleParam(
  search: string,
  pathname: string,
): OnboardingRole | null {
  const params = new URLSearchParams(search || '');
  let roleParam = (
    params.get('role') ||
    params.get('type') ||
    params.get('signupRole') ||
    ''
  ).toLowerCase();
  if (!roleParam && (pathname || '').toLowerCase().includes('/signup/')) {
    const parts = (pathname || '').toLowerCase().split('/');
    const lastPart = parts[parts.length - 1];
    if (isSignupRole(lastPart)) roleParam = lastPart;
  }
  return isSignupRole(roleParam) ? roleParam : null;
}

/**
 * THE canonical effective-roles rule, shared by the server validator, the
 * public signup-roles endpoint, and the client: a stored `signupRoles`
 * value that is not a non-empty array imposes no restriction (all roles);
 * a non-empty array is an allowlist (invalid entries dropped).
 */
export function resolveEffectiveSignupRoles(field: unknown): OnboardingRole[] {
  if (!Array.isArray(field) || field.length === 0) {
    return [...SIGNUP_ROLE_VALUES];
  }
  return field.filter(isSignupRole);
}

/**
 * Interprets the GET /api/auth/signup-roles payload on the client. Returns
 * the workspace's effective set, or null when the payload is unusable
 * (request failed / malformed / filtered to nothing) so the caller falls
 * back instead of rendering a dead-end empty tile grid.
 */
export function normalizeSignupRolesPayload(value: unknown): OnboardingRole[] | null {
  if (!Array.isArray(value)) return null;
  const roles = value.filter(isSignupRole);
  return roles.length > 0 ? roles : null;
}

/**
 * Fallback tile set used only while the workspace's effective roles are
 * unknown (fetch in flight or failed). Preserves the historical
 * brand-flag behavior so a config-fetch failure never makes signup worse
 * than it was before the workspace gate existed. The server still
 * enforces the real workspace set at account creation either way.
 */
export function brandFallbackSignupRoles(roleSetup: {
  employer: boolean;
  referrer: boolean;
  sponsor: boolean;
}): OnboardingRole[] {
  const roles: OnboardingRole[] = ['applicant'];
  if (roleSetup.employer) roles.push('employer');
  if (roleSetup.referrer) roles.push('referrer');
  if (roleSetup.sponsor) roles.push('sponsor');
  return roles;
}

/**
 * The roles the signup tile grid must show: the workspace's effective set
 * when known, otherwise the brand-flag fallback. Exactly the enabled
 * roles — nothing more, nothing less.
 */
export function resolveSignupTileRoles(
  workspaceRoles: readonly OnboardingRole[] | null,
  roleSetup: { employer: boolean; referrer: boolean; sponsor: boolean },
): OnboardingRole[] {
  if (workspaceRoles && workspaceRoles.length > 0) return [...workspaceRoles];
  return brandFallbackSignupRoles(roleSetup);
}

/**
 * Whether a URL pre-set role may skip the role-selection step after email
 * verification. Honored ONLY when the workspace's effective set is known
 * and contains the role: honoring an unconfirmed role could walk the
 * visitor into a server 403, and silently downgrading them to a different
 * role (the old bug turned sponsor-link visitors into applicants) is
 * never acceptable. When not honored, the caller shows role selection
 * with the enabled tiles only — mirroring the server's 403.
 */
export function resolveHonoredPresetRole(
  preset: OnboardingRole | null,
  workspaceRoles: readonly OnboardingRole[] | null,
): OnboardingRole | null {
  if (!preset) return null;
  if (!workspaceRoles) return null;
  return workspaceRoles.includes(preset) ? preset : null;
}

/* ------------------------------------------------------------------ */
/* Returning-user auth routing                                         */
/* ------------------------------------------------------------------ */

export type ReturningUserAuthPath =
  /** Account has a password -> password screen, then mandatory login OTP. */
  | { kind: 'login-password' }
  /** OTP-only account (no password on record) -> login OTP, direct sign-in. */
  | { kind: 'login-otp' }
  /** Unknown email -> signup OTP flow. */
  | { kind: 'signup' };

/**
 * Decides the auth path for an email submitted on the sign-in screen.
 *
 * OTP-only accounts (no password on record) must NEVER be routed into the
 * signup password-setup step: they verify ownership with a LOGIN-purpose
 * code and sign in directly, and the dashboard's role/status routing then
 * decides where they land (e.g. PendingApproval when their organisation
 * was already submitted for review). Routing them into password setup
 * strands them mid-onboarding and hides their real account state.
 */
export function resolveReturningUserAuthPath(
  exists: boolean,
  hasPassword: boolean,
): ReturningUserAuthPath {
  if (!exists) return { kind: 'signup' };
  if (hasPassword) return { kind: 'login-password' };
  return { kind: 'login-otp' };
}

/* ------------------------------------------------------------------ */
/* API error mapping                                                   */
/* ------------------------------------------------------------------ */

export type ConflictKind =
  | 'org-exists'
  | 'account-exists'
  | 'invitation-used'
  | 'unknown';

/**
 * Structured signal for a 409 response. Callers use `conflict` to offer a
 * recovery path (e.g. "continue with the existing organization") instead of
 * a dead-end error banner. `status` carries the application/organization
 * status when the server provides one.
 */
export interface ConflictSignal {
  conflict: ConflictKind;
  status?: string;
  /** Raw server text for debugging only — never render this to users. */
  rawMessage?: string;
}

/**
 * Result of {@link mapApiError}: a user-safe string for every case except
 * HTTP 409, which yields a {@link ConflictSignal} so the UI can recover.
 */
export type ApiErrorResult = string | ConflictSignal;

/** Type guard for the 409 branch of {@link ApiErrorResult}. */
export function isConflictSignal(result: ApiErrorResult): result is ConflictSignal {
  return typeof result !== 'string';
}

/** Patterns that indicate a message contains server internals, never shown. */
const INTERNAL_PATTERNS: RegExp[] = [
  /firebase/i,
  /firestore/i,
  /stack ?trace/i,
  /\bat\s+[A-Za-z_$][\w$.]*\s*\(/,
  /unhandled/i,
  /permission-denied/i,
];

/**
 * Extracts the human-readable `error` field from a JSON error body.
 * Returns null when the text is not a JSON object with a string error —
 * raw JSON must NEVER be shown to users (a 400 body like
 * `{"error":"...","errorCode":"MISSING_FIELDS"}` used to leak verbatim).
 */
export function extractJsonErrorMessage(text: string): string | null {
  const trimmed = (text || '').trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed) as { error?: unknown };
    return typeof parsed?.error === 'string' && parsed.error.trim() ? parsed.error.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Decides whether a server message is safe to show: short, plain language,
 * no stack traces or backend internals. Strips a leading "Error:" prefix.
 * A JSON error body is unwrapped to its `error` field first — users only
 * ever see the sentence, never the raw JSON envelope.
 */
export function sanitizeServerMessage(
  serverMessage: string | undefined,
  fallback: string,
): string {
  if (!serverMessage) return fallback;
  let cleaned = serverMessage.trim().replace(/^error:\s*/i, '');
  if (cleaned.startsWith('{')) {
    const extracted = extractJsonErrorMessage(cleaned);
    if (!extracted) return fallback;
    cleaned = extracted.replace(/^error:\s*/i, '');
  }
  if (cleaned.length < 3 || cleaned.length > 300) return fallback;
  if (INTERNAL_PATTERNS.some((pattern) => pattern.test(cleaned))) return fallback;
  return cleaned;
}

const STATUS_MESSAGES: Record<number, string> = {
  401: 'Your session expired. Please sign in again.',
  403: "You don't have permission to do that.",
  404: "We couldn't find that. It may have expired.",
  410: 'This link or code has expired. Please request a new one.',
  429: 'Too many attempts. Please wait a few minutes and try again.',
};

const SERVER_ERROR_MESSAGE =
  'Something went wrong on our end. Please try again in a moment.';

/**
 * Maps an HTTP status + server message to a user-safe result.
 *
 * - 400 → server message when it looks user-safe, else `fallback`.
 * - 401/403/404/410/429 → fixed messages (server text ignored).
 * - 409 → {@link ConflictSignal} (parsed from `serverMessage`, which may be
 *   a JSON body or plain text) so callers can offer recovery.
 * - 5xx / unknown / network (no status) → generic server-error message.
 *
 * Raw server internals (stack traces, FirebaseError, Firestore, "Error:"
 * prefixes) are never returned.
 */
export function mapApiError(
  status: number | undefined,
  serverMessage: string | undefined,
  fallback: string,
): ApiErrorResult {
  if (status === 400) {
    return sanitizeServerMessage(serverMessage, fallback);
  }
  if (status === 409) {
    let parsed: unknown;
    const trimmed = (serverMessage ?? '').trim();
    if (trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed) as unknown;
      } catch {
        parsed = trimmed;
      }
    } else {
      parsed = trimmed || undefined;
    }
    const interpreted = parseConflict409(parsed);
    return {
      conflict: interpreted?.kind ?? 'unknown',
      status: interpreted?.status,
      rawMessage: trimmed || undefined,
    };
  }
  if (status !== undefined && STATUS_MESSAGES[status] !== undefined) {
    return STATUS_MESSAGES[status];
  }
  if (status === undefined || status >= 500) {
    return SERVER_ERROR_MESSAGE;
  }
  // Any other 4xx: user-safe server text, else the caller-supplied fallback.
  return sanitizeServerMessage(serverMessage, fallback);
}

/* ------------------------------------------------------------------ */
/* 409 conflict interpretation                                         */
/* ------------------------------------------------------------------ */

export interface ParsedConflict {
  kind: ConflictKind;
  status?: string;
}

const CONFLICT_KEYWORDS: { kind: Exclude<ConflictKind, 'unknown'>; pattern: RegExp }[] = [
  {
    kind: 'org-exists',
    pattern: /\b(org(anization)?|company|employer)\b[\s\S]{0,40}\b(already\s+exists?|exists?\s+already|duplicate)\b/i,
  },
  {
    kind: 'account-exists',
    pattern: /\b(account|user|email)\b[\s\S]{0,40}\b(already\s+(exists?|registered|in\s+use)|duplicate)\b/i,
  },
  {
    kind: 'invitation-used',
    pattern: /\binvitation\b[\s\S]{0,40}\b(used|consumed|already\s+accepted|redeemed)\b/i,
  },
];

/**
 * Interprets a 409 response body so callers can offer "continue" recovery
 * instead of a dead end. Accepts the parsed body, a raw string, or nothing.
 * Returns `null` when there is nothing meaningful to interpret.
 */
export function parseConflict409(data: unknown): ParsedConflict | null {
  if (data == null) return null;

  const candidates: string[] = [];
  let status: string | undefined;

  if (typeof data === 'string') {
    if (data.trim()) candidates.push(data);
  } else if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ['message', 'error', 'code', 'detail', 'reason']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) candidates.push(value);
    }
    const rawStatus = record['status'];
    if (typeof rawStatus === 'string' && rawStatus.trim()) status = rawStatus.trim();
  } else {
    return null;
  }

  if (candidates.length === 0) return null;

  for (const text of candidates) {
    for (const { kind, pattern } of CONFLICT_KEYWORDS) {
      if (pattern.test(text)) return { kind, status };
    }
  }
  return { kind: 'unknown', status };
}
